/**
 * Merged Product Column Definitions (Plan 012)
 *
 * Erweitert die Standard-Produkt-Spalten um Readiness + Packshot.
 * Neue Default-OFF-Spalten: gtin, quantity_90d, revenue_90d, readiness_score, issues_count
 */
/* eslint-disable react-refresh/only-export-components -- column definition file with inline cell components */

import { createColumnHelper } from '@tanstack/react-table'
import { Check, Eye, Sparkles } from 'lucide-react'
import type { TFunction } from 'i18next'
import { Badge, Button } from '@/components/v2/ui'
import { ReadinessActionBadge } from './ReadinessActionBadge'
import { PackshotActionCell } from './PackshotActionCell'
import { ReadinessScoreBar } from '@/components/v2/channel-readiness/ReadinessScoreBar'
import type { ProductWithReadiness } from '@/types/products'

const col = createColumnHelper<ProductWithReadiness>()
const eur = (v: number | null | undefined) =>
  v != null ? v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '\u2014'

function SmallBadge({ color, label }: { color: string; label: string }) {
  return (
    <span className={`product-image-badge absolute -top-1 -right-1 h-3 w-3 rounded-full ${color} flex items-center justify-center`}
      aria-label={label}>
      <Sparkles className="h-1.5 w-1.5 text-white" aria-hidden="true" />
    </span>
  )
}

function ProductImageThumb({ p, t }: { p: ProductWithReadiness; t: TFunction }) {
  const noOwn = !p.first_image_id
  const hasAiRef = p.has_ai_ref_packshot && p.ai_ref_packshot_thumbnail_url?.startsWith('/api/')
  const hasCandidate = (p.packshot_status === 'candidate' || p.packshot_status === 'assigned') && p.packshot_thumbnail_url?.startsWith('/api/')
  const url = hasAiRef ? p.ai_ref_packshot_thumbnail_url!
    : hasCandidate ? p.packshot_thumbnail_url!
    : p.first_image_url ?? (p.first_image_id ? `/api/v1/asset/${p.first_image_id}/image` : undefined)
  if (!url) return <div className="product-image-cell h-10 w-10 shrink-0 rounded bg-neutral-100 dark:bg-neutral-800" />
  const ring = p.packshot_status === 'assigned' ? 'ring-2 ring-emerald-400'
    : noOwn && hasAiRef ? 'ring-2 ring-primary-400'
    : noOwn && hasCandidate ? 'ring-2 ring-amber-400'
    : hasCandidate ? 'ring-2 ring-primary-400' : ''
  const title = noOwn && hasAiRef ? t('products.image_no_own_ai_ref')
    : noOwn && hasCandidate ? t('products.image_no_own_candidate') : undefined
  return (
    <div className="product-image-cell relative h-10 w-10 shrink-0" title={title}>
      <img src={url} alt="" loading="lazy" className={`h-10 w-10 rounded object-cover ${ring}`} />
      {p.packshot_status === 'assigned'
        ? <span className="product-image-ki-badge absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-500 flex items-center justify-center">
            <Check className="h-1.5 w-1.5 text-white" aria-hidden="true" />
          </span>
        : noOwn && hasAiRef ? <SmallBadge color="bg-primary-500" label={t('products.image_ki_ref_badge')} />
        : noOwn && hasCandidate ? <SmallBadge color="bg-amber-500" label={t('products.image_ki_candidate_badge')} />
        : hasCandidate ? <SmallBadge color="bg-primary-500" label={t('products.image_ki_candidate_badge')} />
        : null}
    </div>
  )
}

/** Default column visibility — OFF columns require explicit user activation */
export const MERGED_COLUMN_DEFAULTS: Record<string, boolean> = {
  sku: false,
  gtin: false,
  quantity_90d: false,
  revenue_90d: false,
  readiness_score: false,
  issues_count: false,
}

export function createMergedColumns(
  t: TFunction,
  onBadgeClick: (product: ProductWithReadiness) => void,
  onPackshotAssign: (productId: string) => void,
  onOpenQuickView?: (product: ProductWithReadiness) => void,
  onGenerateImage?: (product: ProductWithReadiness) => void,
) {
  return [
    col.accessor('name', {
      header: t('products.col_name'),
      cell: ({ row }) => {
        const p = row.original
        return (
          <div className="product-name-cell flex items-center gap-2 min-w-[200px]">
            <ProductImageThumb p={p} t={t} />
            <div className="min-w-0">
              <span className="text-xs font-medium line-clamp-1">{p.name}</span>
              {p.short_description && (
                <span className="block text-2xs leading-tight text-neutral-500 line-clamp-1">{p.short_description}</span>
              )}
              {p.sku && (
                <span className="block text-2xs leading-tight text-neutral-400 font-mono">{p.sku}</span>
              )}
            </div>
          </div>
        )
      },
    }),
    col.display({
      id: 'readiness_action', header: t('products.col_readiness'),
      cell: ({ row }) => {
        const state = row.original.readiness_action_state
        if (!state) return <span className="text-neutral-400 text-xs">—</span>
        return <ReadinessActionBadge state={state} onClick={() => onBadgeClick(row.original)} />
      },
      enableSorting: false,
    }),
    col.accessor('price_net', {
      header: t('products.col_price_net'),
      cell: ({ getValue }) => <span className="tabular-nums text-xs">{eur(getValue())}</span>,
    }),
    col.accessor('is_active', {
      header: t('products.col_status'),
      cell: ({ getValue }) => (
        <Badge variant={getValue() ? 'success' : 'secondary'} size="sm">
          {getValue() ? t('products.status_active') : t('products.status_inactive')}
        </Badge>
      ),
    }),
    col.accessor('quantity_30d', {
      header: t('products.col_quantity_30d'),
      cell: ({ getValue }) => <span className="tabular-nums text-xs">{getValue() ?? '\u2014'}</span>,
    }),
    col.accessor('revenue_30d', {
      header: t('products.col_revenue_30d'),
      cell: ({ getValue }) => <span className="tabular-nums text-xs">{eur(getValue())}</span>,
    }),
    col.display({
      id: 'packshot', header: t('products.col_packshot'),
      cell: ({ row }) => {
        if (row.original.has_ai_ref_packshot) {
          return (
            <Check className="packshot-action-cell h-5 w-5 text-emerald-500" aria-label={t('products.packshot_ai_ref_set')} />
          )
        }
        return (
          <PackshotActionCell
            packshot_score={row.original.packshot_score}
            packshot_type={row.original.packshot_type}
            packshot_assigned={row.original.packshot_assigned}
            productName={row.original.name}
            onAssign={() => onPackshotAssign(row.original.id)}
          />
        )
      },
      enableSorting: false,
    }),
    // Default OFF:
    col.accessor('sku', {
      header: t('products.col_sku'),
      cell: ({ getValue }) => {
        const v = getValue()
        return v ? <code className="text-2xs">{v}</code> : <span className="text-neutral-400">&mdash;</span>
      },
    }),
    col.accessor('gtin', {
      header: t('products.col_gtin'),
      cell: ({ getValue }) => {
        const v = getValue()
        return v ? <code className="text-2xs">{v}</code> : <span className="text-neutral-400">&mdash;</span>
      },
    }),
    col.accessor('quantity_90d', {
      header: t('products.col_quantity_90d'),
      cell: ({ getValue }) => <span className="tabular-nums text-xs">{getValue() ?? '\u2014'}</span>,
    }),
    col.accessor('revenue_90d', {
      header: t('products.col_revenue_90d'),
      cell: ({ getValue }) => <span className="tabular-nums text-xs">{eur(getValue())}</span>,
    }),
    col.display({
      id: 'readiness_score', header: t('products.col_score'),
      cell: ({ row }) => {
        const score = row.original.readiness_score
        return score != null ? <ReadinessScoreBar score={score} size="sm" showLabel={false} /> : null
      },
      enableSorting: false,
    }),
    col.display({
      id: 'issues_count', header: t('products.col_issues'),
      cell: ({ row }) => {
        const count = row.original.issues_count
        return count ? <Badge variant="error" size="sm">{count}</Badge> : null
      },
      enableSorting: false,
    }),
    col.display({
      id: 'actions', header: '',
      cell: ({ row }) => (
        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
          {onOpenQuickView && (
            <Button variant="outline" size="sm" onClick={() => onOpenQuickView(row.original)} title={t('products.image_quick_view')}>
              <Eye className="h-4 w-4" />
            </Button>
          )}
          {onGenerateImage && (
            <Button variant="outline" size="sm" onClick={() => onGenerateImage(row.original)} title={t('products.image_generate')}>
              <Sparkles className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
      enableSorting: false,
    }),
  ]
}
